// src/pages/Dashboard/sections/network-tabs/NetworkGraphTab.jsx
import React from 'react';
import EnhancedNetworkGraph from '../../../../components/EnhancedNetworkGraph';

/**
 * Network Graph Tab Component
 * Displays the network graph visualization with view options
 */
const NetworkGraphTab = ({
  graphData,
  graphContainerRef,
  viewMode,
  setViewMode,
  relationshipLevel,
  setRelationshipLevel,
  formatWalletAddress,
  formatNumber,
  formatDate
}) => {
  // Helper function to format date with hours
  const formatDateWithHours = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return formatDate ? 
      formatDate(dateString, true) : // Use formatDate with hours if available
      date.toLocaleString(); // Fallback to toLocaleString which includes hours
  };

  // Helper function to determine if a fee is from self-referral
  const isSelfReferral = (node) => {
    if (!node || !node.stability_fees || !node.stability_fees.source_node) return false;
    return node.wallet === node.stability_fees.source_node.wallet_address;
  };

  // Helper function to get fee source label
  const getFeeSourceLabel = (node) => {
    if (!node || !node.stability_fees) return 'No RWA';
    
    if (isSelfReferral(node)) {
      return 'Self-generated';
    }
    
    return node.stability_fees.source_node ? 
      `From ${node.stability_fees.source_node.reference_code || 'Node'}` : 
      'From network';
  };

  return (
    <div className="network-content">
      {graphData.nodes.length > 0 ? (
        <>
          {/* View Mode and Filter Controls */}
          <div className="view-filters">
            <div className="view-mode-toggle">
              <button 
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} 
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 6h13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 12h13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 18h13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M3 6h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M3 12h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              <button 
                className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`} 
                onClick={() => setViewMode('card')}
                title="Card View"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                  <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                  <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                  <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </button>
              <button 
                className={`view-toggle-btn ${viewMode === 'graph' ? 'active' : ''}`} 
                onClick={() => setViewMode('graph')}
                title="Graph View"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="4" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="20" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="6" cy="20" r="2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="18" cy="20" r="2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="6" cy="4" r="2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="18" cy="4" r="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M10 12H6" stroke="currentColor" strokeWidth="1"/>
                  <path d="M18 12H14" stroke="currentColor" strokeWidth="1"/>
                  <path d="M9.5 13.5L7 18" stroke="currentColor" strokeWidth="1"/>
                  <path d="M14.5 13.5L17 18" stroke="currentColor" strokeWidth="1"/>
                  <path d="M9.5 10.5L7 6" stroke="currentColor" strokeWidth="1"/>
                  <path d="M14.5 10.5L17 6" stroke="currentColor" strokeWidth="1"/>
                </svg>
              </button>
            </div>
            
            {viewMode !== 'graph' && (
              <div className="relationship-filter">
                <label>Filter by Relationship Level</label>
                <div className="filter-buttons">
                  <button 
                    className={`filter-btn ${relationshipLevel === 'all' ? 'active' : ''}`}
                    onClick={() => setRelationshipLevel('all')}
                  >
                    All Levels
                  </button>
                  <button 
                    className={`filter-btn ${relationshipLevel === '1' ? 'active' : ''}`}
                    onClick={() => setRelationshipLevel('1')}
                  >
                    Level 1 (Direct)
                  </button>
                  <button 
                    className={`filter-btn ${relationshipLevel === '2' ? 'active' : ''}`}
                    onClick={() => setRelationshipLevel('2')}
                  >
                    Level 2 (Indirect)
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Graph View */}
          {viewMode === 'graph' && (
            <div className="chart-container" ref={graphContainerRef}>
              <EnhancedNetworkGraph 
                data={graphData}
                width={graphContainerRef.current?.clientWidth || 800}
                height={600}
              />
            </div>
          )}
          
          {/* Table View */}
          {viewMode === 'list' && (
            <div className="table-responsive">
              <table className="network-table">
                <thead>
                  <tr>
                    <th>Node</th>
                    <th>Level</th>
                    <th>Tier</th>
                    <th>Wallet Address</th>
                    <th>Network Stability Fees</th>
                    <th>Fee Source</th>
                    <th>Last Updated</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {graphData.nodes
                    .filter(node => !node.isRoot) // Filter out root node
                    .filter(node => relationshipLevel === 'all' || 
                      (relationshipLevel === '1' && node.directSubordinate) ||
                      (relationshipLevel === '2' && node.indirectSubordinate))
                    .map((node, index) => (
                      <tr 
                        key={node.id} 
                        className={`relationship-level-${node.directSubordinate ? '1' : '2'}`}
                      >
                        <td className="node-col">{node.name}</td>
                        <td>
                          <span className={`level-badge level-${node.directSubordinate ? '1' : '2'}`}>
                            Level {node.directSubordinate ? '1' : '2'}
                          </span>
                        </td>
                        <td>{node.tier}</td>
                        <td className="wallet-col">{formatWalletAddress(node.wallet)}</td>
                        <td className="commission-col">
                          {node.stability_fees?.total_amount !== undefined ? 
                            formatNumber(node.stability_fees.total_amount, 2) : 
                            'No RWA'}
                        </td>
                        <td className={`source-col ${isSelfReferral(node) ? 'self-referral' : ''}`}>
                          {getFeeSourceLabel(node)}
                        </td>
                        <td className="date-col">
                          {node.stability_fees?.last_updated ? 
                            formatDateWithHours(node.stability_fees.last_updated) : 
                            'N/A'}
                        </td>
                        <td>
                          <span className={`status-badge active`}>
                            Active
                          </span>
                        </td>
                        <td>
                          <button className="action-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10 12a2 2 0 1 0 4 0 2 2 0 0 0-4 0zm0-6a2 2 0 1 0 4 0 2 2 0 0 0-4 0zm0 12a2 2 0 1 0 4 0 2 2 0 0 0-4 0z" fill="currentColor"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Card View */}
          {viewMode === 'card' && (
            <div className="node-cards-grid">
              {graphData.nodes
                .filter(node => !node.isRoot) // Filter out root node
                .filter(node => relationshipLevel === 'all' || 
                  (relationshipLevel === '1' && node.directSubordinate) ||
                  (relationshipLevel === '2' && node.indirectSubordinate))
                .map((node, index) => (
                  <div 
                    key={node.id} 
                    className={`subordinate-card level-${node.directSubordinate ? '1' : '2'}`}
                  >
                    <div className={`relationship-level-indicator`}>
                      Level {node.directSubordinate ? '1' : '2'}
                    </div>
                    <div className="card-header">
                      <div className="node-code">{node.name}</div>
                      <span className={`status-badge active`}>Active</span>
                    </div>
                    <div className="card-body">
                      <div className="card-row">
                        <span>Tier:</span> {node.tier}
                      </div>
                      <div className="card-row">
                        <span>Wallet:</span> {formatWalletAddress(node.wallet)}
                      </div>
                      <div className="card-row">
                        <span>Network Stability Fees:</span> 
                        <span className="commissions">
                          {node.stability_fees?.total_amount !== undefined ? 
                            formatNumber(node.stability_fees.total_amount, 2) : 
                            'No RWA'}
                        </span>
                      </div>
                      <div className={`card-row ${isSelfReferral(node) ? 'self-referral' : ''}`}>
                        <span>Fee Source:</span>
                        <span className="fee-source">
                          {getFeeSourceLabel(node)}
                        </span>
                      </div>
                      <div className="card-row">
                        <span>Last Updated:</span>
                        <span className="date-value">
                          {node.stability_fees?.last_updated ? 
                            formatDateWithHours(node.stability_fees.last_updated) : 
                            'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="view-details">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12.0004 5C7.52281 5 3.73253 7.94288 2.45825 12C3.73251 16.0571 7.52281 19 12.0004 19C16.4781 19 20.2684 16.0571 21.5426 12C20.2684 7.94291 16.4781 5 12.0004 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      View Details
                    </div>
                  </div>
                ))}
            </div>
          )}
        </>
      ) : (
        <div className="no-data-message">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <p>No network data available for this node. You don't have any subordinate nodes yet.</p>
        </div>
      )}
    </div>
  );
};

export default NetworkGraphTab;
